import { useState, useCallback, useMemo, useEffect } from 'react';
import { Input } from '@/components/ui/Input';
import { MessageThread } from './MessageThread';
import { ClientSearchModal, type ClientSearchResult } from '@/components/ClientSearchModal';
import {
  useMessageThreads,
  useCreateThread,
  type MessageThread as MessageThreadType,
  type ThreadsListParams,
} from './hooks/useMessages';

/** A client and all of their conversation threads. */
interface ClientGroup {
  clientId: string;
  clientName: string;
  clientEmail: string | null;
  threads: MessageThreadType[];
  unreadCount: number;
  lastMessageAt: string;
}

/**
 * Messages page with split layout: thread list on left, active thread on right.
 * Conversations are grouped by client (name + email), each expandable to show
 * that client's individual conversation threads.
 * Validates: Requirements 19.1
 */
export function MessagesPage() {
  const [selectedThread, setSelectedThread] = useState<MessageThreadType | null>(null);
  const [showNewThread, setShowNewThread] = useState(false);
  const [params, setParams] = useState<ThreadsListParams>({
    page: 1,
    pageSize: 50,
    search: '',
    isArchived: false,
  });

  const { data, isLoading } = useMessageThreads(params);
  const createThread = useCreateThread();
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());

  const handleThreadSelect = useCallback((thread: MessageThreadType) => {
    setSelectedThread(thread);
    // Ensure the selected thread's client group is expanded so the thread is
    // visible. This adds to expandedClients (the single source of truth) rather
    // than force-overriding isExpanded at render — otherwise the group could
    // never be collapsed again while its thread stays selected.
    setExpandedClients((prev) => {
      if (prev.has(thread.clientId)) return prev;
      const next = new Set(prev);
      next.add(thread.clientId);
      return next;
    });
  }, []);

  const toggleClient = useCallback((clientId: string) => {
    setExpandedClients((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  }, []);

  // Group threads by client, then apply the search filter (client-side) over
  // client name/email so a client with multiple conversations appears once.
  const clientGroups = useMemo<ClientGroup[]>(() => {
    const items = data?.items ?? [];
    const search = (params.search ?? '').trim().toLowerCase();

    const byClient = new Map<string, ClientGroup>();
    for (const thread of items) {
      let group = byClient.get(thread.clientId);
      if (!group) {
        group = {
          clientId: thread.clientId,
          clientName: thread.clientName,
          clientEmail: thread.clientEmail,
          threads: [],
          unreadCount: 0,
          lastMessageAt: thread.lastMessageAt,
        };
        byClient.set(thread.clientId, group);
      }
      group.threads.push(thread);
      group.unreadCount += thread.unreadCount;
      if (new Date(thread.lastMessageAt) > new Date(group.lastMessageAt)) {
        group.lastMessageAt = thread.lastMessageAt;
      }
    }

    let groups = Array.from(byClient.values());

    if (search) {
      groups = groups.filter(
        (g) =>
          g.clientName.toLowerCase().includes(search) ||
          (g.clientEmail ?? '').toLowerCase().includes(search),
      );
    }

    // Sort clients by most recent activity; keep each client's threads newest-first.
    groups.forEach((g) =>
      g.threads.sort(
        (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime(),
      ),
    );
    groups.sort(
      (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime(),
    );

    return groups;
  }, [data?.items, params.search]);

  // While a search is active, auto-expand every matching client group so the
  // matching conversations are visible. Done via the expandedClients state (the
  // single source of truth) so the accordion toggle still works normally.
  const hasSearch = !!(params.search ?? '').trim();
  useEffect(() => {
    if (!hasSearch) return;
    setExpandedClients((prev) => {
      const next = new Set(prev);
      for (const g of clientGroups) next.add(g.clientId);
      return next;
    });
  }, [hasSearch, clientGroups]);

  function handleCreateThread(clientEmail: string, subject: string, content: string) {
    createThread.mutate(
      { clientEmail, subject, content },
      {
        onSuccess: (newThread) => {
          setShowNewThread(false);
          setSelectedThread(newThread);
        },
      },
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Page header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Messages</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Client conversations and internal notes
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowNewThread(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 transition-colors"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          New Message
        </button>
      </div>

      {/* New Thread Form (overlay) */}
      {showNewThread && (
        <NewThreadForm
          onSubmit={handleCreateThread}
          onCancel={() => setShowNewThread(false)}
          isPending={createThread.isPending}
          error={createThread.isError ? 'Failed to create conversation. Please try again.' : null}
        />
      )}

      {/* Split layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel: Thread list */}
        <aside className="flex w-80 flex-shrink-0 flex-col border-r border-gray-200 dark:border-gray-700">
          {/* Search */}
          <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
            <Input
              placeholder="Search conversations..."
              value={params.search}
              onChange={(e) => setParams((prev) => ({ ...prev, search: e.target.value, page: 1 }))}
            />
          </div>

          {/* Thread list */}
          <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
              </div>
            )}

            {!isLoading && clientGroups.length === 0 && (
              <div className="flex items-center justify-center py-8">
                <p className="text-sm text-gray-500 dark:text-gray-400">No conversations found</p>
              </div>
            )}

            {clientGroups.map((group) => (
              <ClientGroupItem
                key={group.clientId}
                group={group}
                // Expansion is driven solely by expandedClients (the single
                // source of truth). Selecting a thread or searching adds the
                // relevant client to that set, so the toggle always works.
                isExpanded={expandedClients.has(group.clientId)}
                onToggle={() => toggleClient(group.clientId)}
                selectedThreadId={selectedThread?.id ?? null}
                onSelectThread={handleThreadSelect}
              />
            ))}
          </div>
        </aside>

        {/* Right panel: Active thread */}
        <main className="flex flex-1 flex-col overflow-hidden">
          {selectedThread ? (
            <MessageThread thread={selectedThread} />
          ) : (
            <div className="flex flex-1 items-center justify-center">
              <div className="text-center">
                <svg
                  className="mx-auto mb-4 h-12 w-12 text-gray-300 dark:text-gray-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Select a conversation to view messages
                </p>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

// ─── New Thread Form ─────────────────────────────────────────────────────────

interface NewThreadFormProps {
  onSubmit: (clientId: string, subject: string, content: string) => void;
  onCancel: () => void;
  isPending: boolean;
  error: string | null;
}

function NewThreadForm({ onSubmit, onCancel, isPending, error }: NewThreadFormProps) {
  const [selectedClient, setSelectedClient] = useState<ClientSearchResult | null>(null);
  const [showClientSearch, setShowClientSearch] = useState(false);
  const [clientSearchText, setClientSearchText] = useState('');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedClient || !content.trim()) return;
    const email = selectedClient.portalEmail || selectedClient.email || '';
    onSubmit(email, subject.trim(), content.trim());
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-xl border border-gray-200 bg-white p-6 shadow-lg dark:border-gray-700 dark:bg-gray-800">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">New Message</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Start a new conversation with a client.
        </p>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Client
            </label>
            {selectedClient ? (
              <div className="mt-1 flex items-center justify-between rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-700">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {selectedClient.displayName}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{selectedClient.email}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedClient(null)}
                  className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  Change
                </button>
              </div>
            ) : (
              <div className="relative mt-1">
                <input
                  type="text"
                  value={clientSearchText}
                  onChange={(e) => setClientSearchText(e.target.value)}
                  placeholder="Start typing client name or email..."
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                />
                <button
                  type="button"
                  onClick={() => setShowClientSearch(true)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  aria-label="Open client search"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>

          <div>
            <label
              htmlFor="new-thread-subject"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Subject (optional)
            </label>
            <Input
              id="new-thread-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Message subject"
            />
          </div>

          <div>
            <label
              htmlFor="new-thread-content"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Message
            </label>
            <textarea
              id="new-thread-content"
              rows={4}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your message..."
              required
              className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
            />
          </div>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={isPending}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending || !selectedClient || !content.trim()}
              className="inline-flex items-center rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {isPending ? 'Sending...' : 'Send Message'}
            </button>
          </div>
        </form>
      </div>

      {/* Client Search Modal */}
      {showClientSearch && (
        <ClientSearchModal
          onSelect={(client) => {
            setSelectedClient(client);
            setShowClientSearch(false);
          }}
          onClose={() => setShowClientSearch(false)}
          title="Select Client"
        />
      )}
    </div>
  );
}

// ─── Client Group ────────────────────────────────────────────────────────────

interface ClientGroupItemProps {
  group: ClientGroup;
  isExpanded: boolean;
  onToggle: () => void;
  selectedThreadId: string | null;
  onSelectThread: (thread: MessageThreadType) => void;
}

function ClientGroupItem({
  group,
  isExpanded,
  onToggle,
  selectedThreadId,
  onSelectThread,
}: ClientGroupItemProps) {
  const hasUnread = group.unreadCount > 0;

  return (
    <div className="border-b border-gray-100 dark:border-gray-800">
      {/* Client header row */}
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50"
        aria-expanded={isExpanded}
      >
        <svg
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
        {/* Client avatar (person icon) */}
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
            />
          </svg>
        </span>
        <div className="min-w-0 flex-1">
          <p
            className={`truncate text-sm ${
              hasUnread
                ? 'font-semibold text-gray-900 dark:text-gray-100'
                : 'font-medium text-gray-700 dark:text-gray-300'
            }`}
          >
            {group.clientName}
          </p>
          {group.clientEmail && (
            <p className="truncate text-xs text-gray-500 dark:text-gray-400">{group.clientEmail}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {/* Total conversations — neutral, with a chat icon so it reads as a count */}
          <span
            className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-300"
            title={`${group.threads.length} conversation${group.threads.length !== 1 ? 's' : ''}`}
          >
            <svg
              className="h-3 w-3"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 10.5h8M8 14h5m-9 6 3.5-2h9A2.5 2.5 0 0 0 22 15.5v-8A2.5 2.5 0 0 0 19.5 5h-15A2.5 2.5 0 0 0 2 7.5v12l2-2Z"
              />
            </svg>
            {group.threads.length}
          </span>
          {/* New/unread — bold red pill with a pulsing dot to stand out */}
          {hasUnread && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-xs font-semibold text-white dark:bg-red-500"
              title={`${group.unreadCount} new message${group.unreadCount !== 1 ? 's' : ''}`}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-white/90" />
              {group.unreadCount} new
            </span>
          )}
        </div>
      </button>

      {/* Conversation threads under this client */}
      {isExpanded && (
        <div className="bg-gray-50/50 dark:bg-gray-900/30">
          {group.threads.map((thread) => (
            <ThreadListItem
              key={thread.id}
              thread={thread}
              isActive={selectedThreadId === thread.id}
              onClick={() => onSelectThread(thread)}
              indented
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Thread List Item ────────────────────────────────────────────────────────

interface ThreadListItemProps {
  thread: MessageThreadType;
  isActive: boolean;
  onClick: () => void;
  indented?: boolean;
}

function ThreadListItem({ thread, isActive, onClick, indented = false }: ThreadListItemProps) {
  const hasUnread = thread.unreadCount > 0;
  // Primary label: the conversation subject (client name lives on the group header).
  const title = thread.subject?.trim() || 'Conversation';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full border-b border-gray-100 py-2.5 pr-4 text-left transition-colors dark:border-gray-800
        ${indented ? 'pl-10' : 'pl-4'}
        ${
          isActive
            ? 'bg-brand-50 dark:bg-brand-900/20'
            : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
        }`}
      aria-current={isActive ? 'true' : undefined}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          {/* Conversation subject + direction chip */}
          <div className="flex items-center gap-2">
            <p
              className={`min-w-0 truncate text-sm ${
                hasUnread
                  ? 'font-semibold text-gray-900 dark:text-gray-100'
                  : 'font-medium text-gray-700 dark:text-gray-300'
              }`}
            >
              {title}
            </p>
            {thread.lastMessageByStaff ? (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                <svg
                  className="h-3 w-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 8.688c0-.864.933-1.406 1.683-.977l7.108 4.062a1.125 1.125 0 010 1.954l-7.108 4.062A1.125 1.125 0 013 16.874V8.688zM12.75 8.688c0-.864.933-1.406 1.683-.977l7.108 4.062a1.125 1.125 0 010 1.954l-7.108 4.062a1.125 1.125 0 01-1.683-.977V8.688z"
                  />
                </svg>
                Replied
              </span>
            ) : (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                <svg
                  className="h-3 w-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                  />
                </svg>
                From client
              </span>
            )}
          </div>

          {/* Last message preview */}
          <p
            className={`mt-1 truncate text-xs ${
              hasUnread ? 'text-gray-700 dark:text-gray-300' : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            {thread.lastMessagePreview}
          </p>
        </div>

        <div className="flex flex-col items-end gap-1">
          {/* Timestamp */}
          <span className="whitespace-nowrap text-xs text-gray-400 dark:text-gray-500">
            {formatRelativeTime(thread.lastMessageAt)}
          </span>

          {/* New/unread badge — solid red to clearly signal new messages */}
          {hasUnread && (
            <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-600 px-1.5 py-0.5 text-xs font-semibold text-white dark:bg-red-500">
              {thread.unreadCount}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Format a date as relative time (e.g., "2h ago", "Yesterday").
 */
function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
